import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

// ── Design tokens ────────────────────────────────────────────────────────────
const C = {
  navy:   '#0D2B45',
  blue:   '#1a5fa8',
  sky:    '#2d9de8',
  teal:   '#0ab5a0',
  purple: '#5b3fa6',
  gold:   '#e8a020',
  green:  '#059669',
  red:    '#dc2626',
  white:  '#ffffff',
  off:    '#f7f9fc',
  slate:  '#64748b',
  dark:   '#0a1628',
};

const TIER_COLORS = {
  SONNET: { bg: '#fef3c7', text: '#d97706', border: '#fde68a' },
  HAIKU:  { bg: '#d1fae5', text: '#059669', border: '#a7f3d0' },
  FREE:   { bg: '#f1f5f9', text: '#64748b', border: '#e2e8f0' },
};

const STATUS_COLORS = {
  trialing: { bg: '#ede9fe', text: '#7c3aed', border: '#ddd6fe' },
  active:   { bg: '#d1fae5', text: '#059669', border: '#a7f3d0' },
  inactive: { bg: '#fee2e2', text: '#dc2626', border: '#fecaca' },
  '':       { bg: '#f1f5f9', text: '#64748b', border: '#e2e8f0' },
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=DM+Sans:wght@300;400;500;600;700&display=swap');
  .aum-table { width:100%; border-collapse:collapse; }
  .aum-table th { padding:12px 16px; text-align:left; font-size:11px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:#64748b; border-bottom:2px solid #e2e8f0; white-space:nowrap; }
  .aum-table td { padding:14px 16px; border-bottom:1px solid #f1f5f9; font-size:14px; color:#1e293b; vertical-align:middle; }
  .aum-table tr:hover td { background:#f8fafc; }
  .aum-input { width:100%; padding:9px 12px; border:1px solid #e2e8f0; border-radius:8px; font-size:14px; font-family:'DM Sans',system-ui,sans-serif; outline:none; transition:border-color .2s; }
  .aum-input:focus { border-color:#2d9de8; box-shadow:0 0 0 3px rgba(45,157,232,.1); }
  .aum-select { padding:9px 12px; border:1px solid #e2e8f0; border-radius:8px; font-size:13px; font-family:'DM Sans',system-ui,sans-serif; outline:none; cursor:pointer; background:white; }
  .aum-select:focus { border-color:#2d9de8; }
  .aum-btn { display:inline-flex; align-items:center; gap:6px; padding:8px 16px; border-radius:8px; font-family:'DM Sans',system-ui,sans-serif; font-weight:600; font-size:13px; cursor:pointer; border:none; transition:all .2s; }
  .aum-btn:hover { transform:translateY(-1px); }
  .aum-btn-primary { background:linear-gradient(135deg,#1a5fa8,#2d9de8); color:white; box-shadow:0 4px 12px rgba(45,157,232,.3); }
  .aum-btn-success { background:linear-gradient(135deg,#059669,#0ab5a0); color:white; box-shadow:0 4px 12px rgba(10,181,160,.3); }
  .aum-btn-danger  { background:linear-gradient(135deg,#dc2626,#ef4444); color:white; }
  .aum-btn-ghost   { background:#f1f5f9; color:#475569; border:1px solid #e2e8f0; }
  .aum-pill { display:inline-flex; align-items:center; padding:3px 10px; border-radius:999px; font-size:11px; font-weight:700; letter-spacing:.05em; text-transform:uppercase; border:1px solid; }
  .aum-modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.5); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:24px; }
  .aum-modal { background:white; border-radius:20px; padding:36px; max-width:560px; width:100%; box-shadow:0 24px 80px rgba(0,0,0,.2); }
  .aum-card { background:white; border-radius:16px; padding:24px; box-shadow:0 2px 16px rgba(13,43,69,.06); border:1px solid #f1f5f9; }
  .aum-toast { position:fixed; bottom:24px; right:24px; padding:14px 20px; border-radius:12px; font-family:'DM Sans',system-ui,sans-serif; font-size:14px; font-weight:600; color:white; z-index:9999; animation:toastIn .3s ease; box-shadow:0 8px 24px rgba(0,0,0,.2); }
  @keyframes toastIn { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin { to{transform:rotate(360deg)} }
  .aum-spinner { width:18px; height:18px; border:2px solid rgba(255,255,255,.3); border-top-color:white; border-radius:50%; animation:spin .7s linear infinite; }
`;

// ── Tier Badge ────────────────────────────────────────────────────────────────
function TierBadge({ tier }) {
  const c = TIER_COLORS[tier] || TIER_COLORS.FREE;
  return (
    <span className="aum-pill" style={{ background: c.bg, color: c.text, borderColor: c.border }}>
      {tier === 'SONNET' ? '⭐' : tier === 'HAIKU' ? '✨' : '○'} {tier || 'FREE'}
    </span>
  );
}

// ── Status Badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS[''];
  return (
    <span className="aum-pill" style={{ background: c.bg, color: c.text, borderColor: c.border }}>
      {status || 'none'}
    </span>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className="aum-toast" style={{ background: type === 'success' ? C.green : type === 'error' ? C.red : C.blue }}>
      {type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️'} {message}
    </div>
  );
}

// ── Edit User Modal ───────────────────────────────────────────────────────────
function EditUserModal({ user, onSave, onClose, saving }) {
  const [form, setForm] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    tier: user.tier || 'FREE',
    status: user.status || 'trialing',
    ckdStage: user.ckdStage || '',
    dialysisModality: user.dialysisModality || '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="aum-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="aum-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, color: C.navy, margin: 0 }}>
            Edit User
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: C.slate }}>×</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.slate, display: 'block', marginBottom: 5 }}>FIRST NAME</label>
            <input className="aum-input" value={form.firstName} onChange={e => set('firstName', e.target.value)} placeholder="First name" />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.slate, display: 'block', marginBottom: 5 }}>LAST NAME</label>
            <input className="aum-input" value={form.lastName} onChange={e => set('lastName', e.target.value)} placeholder="Last name" />
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.slate, display: 'block', marginBottom: 5 }}>EMAIL</label>
          <input className="aum-input" value={form.email} onChange={e => set('email', e.target.value)} placeholder="Email address" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.slate, display: 'block', marginBottom: 5 }}>TIER</label>
            <select className="aum-select" style={{ width: '100%' }} value={form.tier} onChange={e => set('tier', e.target.value)}>
              <option value="FREE">FREE</option>
              <option value="HAIKU">HAIKU</option>
              <option value="SONNET">SONNET</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.slate, display: 'block', marginBottom: 5 }}>STATUS</label>
            <select className="aum-select" style={{ width: '100%' }} value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="trialing">trialing</option>
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.slate, display: 'block', marginBottom: 5 }}>CKD STAGE</label>
            <select className="aum-select" style={{ width: '100%' }} value={form.ckdStage} onChange={e => set('ckdStage', e.target.value)}>
              <option value="">Unknown</option>
              <option value="1">Stage 1</option>
              <option value="2">Stage 2</option>
              <option value="3a">Stage 3a</option>
              <option value="3b">Stage 3b</option>
              <option value="4">Stage 4</option>
              <option value="5">Stage 5</option>
              <option value="5D">Stage 5D (Dialysis-dependent)</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.slate, display: 'block', marginBottom: 5 }}>DIALYSIS MODALITY</label>
            <select className="aum-select" style={{ width: '100%' }} value={form.dialysisModality} onChange={e => set('dialysisModality', e.target.value)}>
              <option value="">Unknown</option>
              <option value="Pre-dialysis">Pre-dialysis</option>
              <option value="Hemodialysis">Hemodialysis</option>
              <option value="Peritoneal Dialysis">Peritoneal Dialysis</option>
              <option value="Transplant">Transplant</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="aum-btn aum-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="aum-btn aum-btn-primary" onClick={() => onSave(user.userId, form)} disabled={saving}>
            {saving ? <><div className="aum-spinner" /> Saving...</> : '💾 Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Create User Modal ─────────────────────────────────────────────────────────
function CreateUserModal({ onSave, onClose, saving }) {
  const [form, setForm] = useState({
    userId: '', firstName: '', lastName: '', email: '',
    tier: 'FREE', status: 'trialing', ckdStage: '', dialysisModality: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="aum-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="aum-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, color: C.navy, margin: 0 }}>
            Create Patient Record
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: C.slate }}>×</button>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.slate, display: 'block', marginBottom: 5 }}>AUTH0 USER ID *</label>
          <input className="aum-input" value={form.userId} onChange={e => set('userId', e.target.value)} placeholder="auth0|xxxx or google-oauth2|xxxx" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.slate, display: 'block', marginBottom: 5 }}>FIRST NAME *</label>
            <input className="aum-input" value={form.firstName} onChange={e => set('firstName', e.target.value)} placeholder="First name" />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.slate, display: 'block', marginBottom: 5 }}>LAST NAME *</label>
            <input className="aum-input" value={form.lastName} onChange={e => set('lastName', e.target.value)} placeholder="Last name" />
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.slate, display: 'block', marginBottom: 5 }}>EMAIL *</label>
          <input className="aum-input" value={form.email} onChange={e => set('email', e.target.value)} placeholder="user@email.com" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.slate, display: 'block', marginBottom: 5 }}>TIER</label>
            <select className="aum-select" style={{ width: '100%' }} value={form.tier} onChange={e => set('tier', e.target.value)}>
              <option value="FREE">FREE</option>
              <option value="HAIKU">HAIKU</option>
              <option value="SONNET">SONNET</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.slate, display: 'block', marginBottom: 5 }}>CKD STAGE</label>
            <select className="aum-select" style={{ width: '100%' }} value={form.ckdStage} onChange={e => set('ckdStage', e.target.value)}>
              <option value="">Unknown</option>
              <option value="1">Stage 1</option>
              <option value="2">Stage 2</option>
              <option value="3a">Stage 3a</option>
              <option value="3b">Stage 3b</option>
              <option value="4">Stage 4</option>
              <option value="5">Stage 5</option>
              <option value="5D">Stage 5D (Dialysis-dependent)</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="aum-btn aum-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="aum-btn aum-btn-success" onClick={() => onSave(form)} disabled={saving || !form.userId || !form.firstName || !form.email}>
            {saving ? <><div className="aum-spinner" /> Creating...</> : '➕ Create Patient'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
const AdminUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTier, setFilterTier] = useState('ALL');
  const [editUser, setEditUser] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/admin/users');
      setUsers(response.data);
    } catch (e) {
      showToast('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSaveEdit = async (userId, form) => {
    try {
      setSaving(true);
      await api.put(`/v1/admin/users/${userId}`, form);
      showToast(`${form.firstName} ${form.lastName} updated successfully`);
      setEditUser(null);
      fetchUsers();
    } catch (e) {
      showToast('Failed to update user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async (form) => {
    try {
      setSaving(true);
      await api.post('/v1/admin/users', form);
      showToast(`${form.firstName} ${form.lastName} created successfully`);
      setShowCreate(false);
      fetchUsers();
    } catch (e) {
      showToast('Failed to create user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTierChange = async (userId, tier) => {
    try {
      await api.patch(`/v1/admin/users/${userId}/tier`, { tier });
      showToast(`Tier updated to ${tier}`);
      fetchUsers();
    } catch (e) {
      showToast('Failed to update tier', 'error');
    }
  };

  const handleResetPassword = async (email) => {
    try {
      await api.post('/v1/admin/users/reset-password', { email });
      showToast(`Password reset email sent to ${email}`);
    } catch (e) {
      showToast('Failed to send reset email', 'error');
    }
  };

  // Filter users
  const filtered = users.filter(u => {
    const matchesSearch = !search ||
      `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(search.toLowerCase());
    const matchesTier = filterTier === 'ALL' || u.tier === filterTier;
    return matchesSearch && matchesTier;
  });

  // Stats
  const stats = {
    total: users.length,
    sonnet: users.filter(u => u.tier === 'SONNET').length,
    haiku: users.filter(u => u.tier === 'HAIKU').length,
    free: users.filter(u => !u.tier || u.tier === 'FREE').length,
    withProfile: users.filter(u => u.hasPatientRecord).length,
  };

  return (
    <div style={{ fontFamily: "'DM Sans',system-ui,sans-serif", background: C.off, minHeight: '100vh', padding: '32px 24px' }}>
      <style>{STYLES}</style>

      {/* Header */}
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
          <div>
            <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 700, color: C.navy, margin: '0 0 6px' }}>
              User Management
            </h1>
            <p style={{ fontSize: 14, color: C.slate, margin: 0 }}>
              Manage patient records, subscription tiers, and access
            </p>
          </div>
          <button className="aum-btn aum-btn-primary" onClick={() => setShowCreate(true)}>
            ➕ Create Patient Record
          </button>
        </div>

        {/* Stats cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 16, marginBottom: 28 }}>
          {[
            { label: 'Total Users', value: stats.total, color: C.blue, icon: '👥' },
            { label: 'Sonnet', value: stats.sonnet, color: C.gold, icon: '⭐' },
            { label: 'Haiku', value: stats.haiku, color: C.green, icon: '✨' },
            { label: 'Free', value: stats.free, color: C.slate, icon: '○' },
            { label: 'With Profile', value: stats.withProfile, color: C.teal, icon: '✅' },
          ].map((s, i) => (
            <div key={i} className="aum-card" style={{ textAlign: 'center', padding: '20px 16px' }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.slate, textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="aum-card" style={{ marginBottom: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
          <input
            className="aum-input"
            style={{ maxWidth: 320 }}
            placeholder="🔍 Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select className="aum-select" value={filterTier} onChange={e => setFilterTier(e.target.value)}>
            <option value="ALL">All Tiers</option>
            <option value="SONNET">SONNET</option>
            <option value="HAIKU">HAIKU</option>
            <option value="FREE">FREE</option>
          </select>
          <button className="aum-btn aum-btn-ghost" onClick={fetchUsers}>
            🔄 Refresh
          </button>
          <span style={{ marginLeft: 'auto', fontSize: 13, color: C.slate }}>
            {filtered.length} of {users.length} users
          </span>
        </div>

        {/* Table */}
        <div className="aum-card" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 60, textAlign: 'center', color: C.slate }}>
              <div className="aum-spinner" style={{ margin: '0 auto 12px', borderColor: 'rgba(100,116,139,.3)', borderTopColor: C.blue }} />
              Loading users...
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="aum-table">
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Auth0 ID</th>
                    <th>Amyloid Type</th>
                    <th>Stage</th>
                    <th>Tier</th>
                    <th>Status</th>
                    <th>Profile</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(user => (
                    <tr key={user.userId}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: '50%',
                            background: `linear-gradient(135deg,${C.blue},${C.sky})`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 14, fontWeight: 700, color: 'white', flexShrink: 0,
                          }}>
                            {(user.firstName?.[0] || '?').toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: C.navy }}>{user.firstName} {user.lastName}</div>
                            <div style={{ fontSize: 11, color: C.slate }}>
                              {user.userId?.startsWith('google') ? '🔵 Google' : '🔐 Auth0'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ color: C.slate }}>{user.email}</td>
                      <td>
                        <code style={{ fontSize: 11, background: '#f1f5f9', padding: '3px 7px', borderRadius: 5, color: C.slate }}>
                          {user.userId?.substring(0, 20)}...
                        </code>
                      </td>
                      <td>
                        {user.ckdStage ? (
                          <span style={{ fontSize: 13, fontWeight: 600, color: C.blue }}>{user.ckdStage}</span>
                        ) : (
                          <span style={{ fontSize: 12, color: '#cbd5e1' }}>—</span>
                        )}
                      </td>
                      <td style={{ fontSize: 13, color: C.slate }}>{user.dialysisModality || '—'}</td>
                      <td>
                        <select
                          className="aum-select"
                          value={user.tier || 'FREE'}
                          onChange={e => handleTierChange(user.userId, e.target.value)}
                          style={{ padding: '4px 8px', fontSize: 12 }}
                        >
                          <option value="FREE">FREE</option>
                          <option value="HAIKU">HAIKU</option>
                          <option value="SONNET">SONNET</option>
                        </select>
                      </td>
                      <td><StatusBadge status={user.status} /></td>
                      <td>
                        <span style={{
                          fontSize: 12, fontWeight: 600,
                          color: user.hasPatientRecord ? C.green : C.red,
                        }}>
                          {user.hasPatientRecord ? '✅ Complete' : '❌ Missing'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className="aum-btn aum-btn-ghost"
                            style={{ padding: '5px 10px', fontSize: 12 }}
                            onClick={() => setEditUser(user)}
                            title="Edit user"
                          >✏️</button>
                          {user.userId?.startsWith('auth0|') && (
                            <button
                              className="aum-btn aum-btn-ghost"
                              style={{ padding: '5px 10px', fontSize: 12 }}
                              onClick={() => handleResetPassword(user.email)}
                              title="Send password reset email"
                            >🔑</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={9} style={{ textAlign: 'center', padding: 40, color: C.slate }}>
                        No users found matching your search
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {editUser && (
        <EditUserModal
          user={editUser}
          onSave={handleSaveEdit}
          onClose={() => setEditUser(null)}
          saving={saving}
        />
      )}
      {showCreate && (
        <CreateUserModal
          onSave={handleCreate}
          onClose={() => setShowCreate(false)}
          saving={saving}
        />
      )}

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default AdminUserManagement;